import React, { useState } from 'react';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import {
  WebGalleryRoot,
  WebGalleryMainImage,
  WebGalleryGridItem,
  WebGalleryShowAll,
  WebGalleryMask,
  WebGalleryCount,
} from './style';
import FullScreenImages from './FullScreenImages';
import { Box } from '@mui/material';
import Image from 'next/image';
import useStoreApp from '@/store';
import { TourImageDetail } from '@/types/tourBooking';
import { timeStamp } from 'console';
import useEmbed, { PostMessageType } from '@/hooks/useEmbed';

interface WebGalleryProps {
  images: TourImageDetail[];
  alt?: string;
}

const WebGallery: React.FC<WebGalleryProps> = ({ images, alt }) => {

  const { isEmbeed } = useStoreApp(state => state.globalSlice);

  const [fullScreen, setFullScreen] = useState(false);
  const [fullScreenIdx, setFullScreenIdx] = useState(0);

  const { sendPostMessage } = useEmbed();

  if (!images || images.length === 0) return null;

  // Prepare grid items
  const mainImage = images[0];
  const smallImages = images.slice(1, 5);
  const total = images.length;
  const showAll = total > 5;
  const showAllCount = total - 5;
  const emptySlots = 4 - smallImages.length;
  let gridImages: (TourImageDetail | 'SHOW_ALL' | null)[] = smallImages;
  if (!showAll && emptySlots > 0) {
    gridImages = [...smallImages, ...Array(emptySlots).fill(null)];
  } else if (showAll) {
    gridImages = [...smallImages.slice(0, 3), 'SHOW_ALL'];
  }

  // Handlers
  const handleImageClick = (idx: number) => {
    if (isEmbeed) {
      const payloadPostMessage = {
        payload: {
          selectedIdx: idx,
          images: images
        }
      }
      sendPostMessage(PostMessageType.open_imgs_overlay, payloadPostMessage);
    }
    else {
      setFullScreenIdx(idx);
      setFullScreen(true);
    }
  };

  const handleImageKeyDown = (idx: number) => (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      setFullScreenIdx(idx);
      setFullScreen(true);
    }
  };

  return (
    <WebGalleryRoot>
      {/* Main (biggest) image */}
      <Box
        component="div"
        tabIndex={0}
        onClick={() => handleImageClick(0)}
        onKeyDown={handleImageKeyDown(0)}
        aria-label="Open full screen image"
        sx={{
          gridRow: '1 / span 2',
          gridColumn: '1 / span 1',
          position: 'relative',
          borderRadius: "16px",
          overflow: 'hidden',
        }}
      >
        <WebGalleryMainImage
          src={mainImage.url}
          alt={mainImage.alt && mainImage.alt !== '' ? mainImage.alt : (alt && alt !== '' ? alt : 'Gallery')}
        />
      </Box>
      {/* Small images (2x2 grid) */}
      {gridImages.map((img, idx) => {
        const gridCol = idx % 2 === 0 ? 2 : 3;
        const gridRow = idx < 2 ? 1 : 2;
        if (img === 'SHOW_ALL') {
          return (
            <WebGalleryShowAll
              key="show-all"
              tabIndex={0}
              onClick={() => handleImageClick(4)}
              onKeyDown={handleImageKeyDown(4)}
              sx={{ gridColumn: gridCol, gridRow: gridRow }}
            >
              <Image
                src={images[4].url ?? images[images.length - 1].url ?? ''}
                alt={images[4].alt && images[4].alt !== '' ? images[4].alt : (images[images.length - 1].alt && images[images.length - 1].alt !== '' ? images[images.length - 1].alt ?? 'show-all' : "show-all")}
                width={0}
                height={0}
                sizes="100vw"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  borderRadius: 12,
                  filter: 'blur(1px) brightness(0.7)'
                }}
              />
              <WebGalleryMask>
                <WebGalleryCount>+{showAllCount + 1}</WebGalleryCount>
              </WebGalleryMask>
            </WebGalleryShowAll>
          );
        } else if (img) {
          return (
            <WebGalleryGridItem
              key={`img-${idx}`}
              src={img.url}
              alt={img.alt && img.alt !== '' ? img.alt : `grid-${idx}`}
              tabIndex={0}
              onClick={() => handleImageClick(idx + 1)}
              onKeyDown={handleImageKeyDown(idx + 1)}
              sx={{ gridColumn: gridCol, gridRow: gridRow }}
            />
          );
        } else {
          // Empty slot
          return <Box
            key={`empty-${idx}`}
            sx={{
              gridColumn: gridCol,
              gridRow: gridRow,
              width: '100%',
              height: '100%',
              borderRadius: 2,
              background: theme => theme.palette.blue.background
            }} />;
        }
      })}
      <FullScreenImages
        open={fullScreen}
        images={images}
        selectedIdx={fullScreenIdx}
        onClose={() => setFullScreen(false)}
        onSelect={setFullScreenIdx}
      />
    </WebGalleryRoot>
  );
};

export default WebGallery; 